import axios from "axios";
import { spotViewerAuthorizationHeader } from "../constants/spotViewerAuth";
import { httpClient } from "../http/client";
import { sortSpotRoundsBySequenceAsc } from "./spotRoundSort";

type UnknownRecord = Record<string, unknown>;

export type ViewerSeatValue = number | "";
export type ViewerSeatMatrix = {
  jee: Record<string, Record<string, ViewerSeatValue>>;
  mhtcet: Record<string, Record<string, ViewerSeatValue>>;
};
export type ViewerRoundStatus = "Active" | "Ended" | "Not Started";
export type SpotMatrixSection = "jee" | "mhtcet";

export interface ViewerRoundOut {
  id: number;
  previousRoundId: number | null;
  startDate: string;
  endDate: string;
  status: string;
  name?: string;
}

export interface ViewerSpotOut {
  id: number;
  spotRoundId: number | null;
  exam: string;
  courseName: string;
  courseShortForm: string | null;
  courseCode: string;
  courseId: number | null;
  category: string;
  vacantSeatsCount: number | null;
  updatedBy?: string;
  updatedOn?: string;
}

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === "object" ? (value as UnknownRecord) : null;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function toStringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function toRoundDateValue(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const parsed = new Date(ms);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }
  if (Array.isArray(value) && value.length >= 3) {
    const year = Number(value[0]);
    const month = Number(value[1]);
    const day = Number(value[2]);
    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return "";
}

function padDatePart(value: number): string {
  return String(Math.trunc(value)).padStart(2, "0");
}

/** Preserve a full UTC instant for spot `updatedOn` (do not strip to a calendar date). */
function toUpdatedOnUtcValue(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const parsed = new Date(ms);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }
  if (Array.isArray(value) && value.length >= 3) {
    const year = Number(value[0]);
    const month = Number(value[1]);
    const day = Number(value[2]);
    const hour = Number(value[3] ?? 0);
    const minute = Number(value[4] ?? 0);
    const second = Number(value[5] ?? 0);
    if (
      !Number.isFinite(year)
      || !Number.isFinite(month)
      || !Number.isFinite(day)
      || !Number.isFinite(hour)
      || !Number.isFinite(minute)
      || !Number.isFinite(second)
    ) {
      return "";
    }
    return `${year}-${padDatePart(month)}-${padDatePart(day)}T${padDatePart(hour)}:${padDatePart(minute)}:${padDatePart(second)}Z`;
  }
  return "";
}

function pickRoundDateFromRecords(
  records: Array<UnknownRecord | null | undefined>,
  keys: string[],
): string {
  for (const record of records) {
    if (!record) {
      continue;
    }
    for (const key of keys) {
      const value = toRoundDateValue(record[key]);
      if (value) {
        return value;
      }
    }
  }
  return "";
}

function pickStringFromRecords(
  records: Array<UnknownRecord | null | undefined>,
  keys: string[],
): string {
  for (const record of records) {
    if (!record) {
      continue;
    }
    for (const key of keys) {
      const value = toStringValue(record[key]);
      if (value) {
        return value;
      }
    }
  }
  return "";
}

function normalizeLookupKey(key: string): string {
  return key
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function toIdNumber(value: unknown): number | null {
  const direct = toNumber(value);
  if (direct !== null) {
    return direct;
  }
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  return (
    toNumber(record.spotDetailId)
    ?? toNumber(record.spot_detail_id)
    ?? toNumber(record.courseId)
    ?? toNumber(record.course_id)
    ?? toNumber(record.id)
  );
}

function pickNumberFromRecords(
  records: Array<UnknownRecord | null | undefined>,
  keys: string[],
): number | null {
  const normalizedWanted = new Set(keys.map((key) => normalizeLookupKey(key)));
  for (const record of records) {
    if (!record) {
      continue;
    }
    for (const key of keys) {
      const value = toIdNumber(record[key]);
      if (value !== null) {
        return value;
      }
    }
    for (const [key, value] of Object.entries(record)) {
      if (!normalizedWanted.has(normalizeLookupKey(key))) {
        continue;
      }
      const parsed = toIdNumber(value);
      if (parsed !== null) {
        return parsed;
      }
    }
  }
  return null;
}

function getViewerApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Unable to connect to server.";
    }

    const status = error.response.status;
    if (status === 401) {
      return "Authentication failed.";
    }
    if (status === 403) {
      return "Access denied.";
    }

    const responseData = error.response.data;
    if (typeof responseData === "string" && responseData.trim()) {
      return responseData.trim();
    }
    if (responseData && typeof responseData === "object") {
      const typed = responseData as { message?: unknown; detail?: unknown; error?: unknown };
      if (typeof typed.message === "string" && typed.message.trim()) {
        return typed.message.trim();
      }
      if (typeof typed.detail === "string" && typed.detail.trim()) {
        return typed.detail.trim();
      }
      if (typeof typed.error === "string" && typed.error.trim()) {
        return typed.error.trim();
      }
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function normalizeViewerRound(raw: unknown): ViewerRoundOut | null {
  const root = asRecord(raw);
  if (!root) {
    return null;
  }

  const nested =
    asRecord(root.round)
    ?? asRecord(root.data)
    ?? asRecord(root.result)
    ?? root;

  const id =
    toNumber(nested.id)
    ?? toNumber(nested.roundId)
    ?? toNumber(nested.round_id)
    ?? toNumber(root.id)
    ?? toNumber(root.roundId)
    ?? toNumber(root.round_id);

  if (id === null) {
    return null;
  }

  const previousRaw =
    nested.previousRoundId
    ?? nested.previous_round_id
    ?? root.previousRoundId
    ?? root.previous_round_id;
  const previousRoundId =
    previousRaw === null || previousRaw === undefined || previousRaw === ""
      ? null
      : toNumber(previousRaw);

  const startDate = pickRoundDateFromRecords([nested, root], [
    "startDate",
    "start_date",
    "dayStart",
    "day_start",
  ]);
  const endDate = pickRoundDateFromRecords([nested, root], [
    "endDate",
    "end_date",
    "dayEnd",
    "day_end",
  ]);
  const status =
    pickStringFromRecords([nested, root], ["status", "roundStatus", "round_status"]) || "Not Started";
  const name = pickStringFromRecords([nested, root], ["name", "title", "roundName", "round_name"]);

  return {
    id,
    previousRoundId: previousRoundId === null ? null : previousRoundId,
    startDate,
    endDate,
    status,
    name: name || undefined,
  };
}

function parseViewerRoundsResponse(raw: unknown): ViewerRoundOut[] {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => normalizeViewerRound(item))
      .filter((item): item is ViewerRoundOut => Boolean(item));
  }

  const root = asRecord(raw);
  if (!root) {
    throw new Error("Invalid rounds response.");
  }

  const candidates = [
    root.rounds,
    root.content,
    root.items,
    root.data,
    root.results,
    asRecord(root.data)?.rounds,
    asRecord(root.data)?.content,
    asRecord(root.result)?.rounds,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate
        .map((item) => normalizeViewerRound(item))
        .filter((item): item is ViewerRoundOut => Boolean(item));
    }
  }

  const single = normalizeViewerRound(raw);
  if (single) {
    return [single];
  }

  throw new Error("Invalid rounds response.");
}

function normalizeViewerSpot(raw: unknown): ViewerSpotOut | null {
  const root = asRecord(raw);
  if (!root) {
    return null;
  }

  const nested =
    asRecord(root.spotDetail)
    ?? asRecord(root.spot_detail)
    ?? asRecord(root.spot)
    ?? asRecord(root.data)
    ?? asRecord(root.result)
    ?? root;

  const spotDetailId = pickNumberFromRecords([nested, root], [
    "spotDetailId",
    "spot_detail_id",
  ]);
  const genericId = pickNumberFromRecords([nested, root], [
    "spotId",
    "spot_id",
    "id",
  ]);
  const id = spotDetailId ?? genericId;

  const spotRoundId = pickNumberFromRecords([nested, root], [
    "spotRoundId",
    "spot_round_id",
    "roundId",
    "round_id",
  ]);

  const exam = pickStringFromRecords([nested, root], [
    "exam",
    "examName",
    "exam_name",
    "examType",
    "exam_type",
  ]);

  const courseRecord =
    asRecord(nested.course)
    ?? asRecord(root.course)
    ?? asRecord(nested.courseInfo)
    ?? asRecord(root.courseInfo)
    ?? asRecord(nested.courseId)
    ?? asRecord(root.courseId);

  const courseName =
    pickStringFromRecords([nested, root, courseRecord], [
      "courseName",
      "course_name",
      "stream",
      "streamName",
      "stream_name",
    ])
    || (courseRecord ? toStringValue(courseRecord.name) : "");

  const courseShortFormRaw = pickStringFromRecords([nested, root, courseRecord], [
    "courseShortForm",
    "course_short_form",
    "courseShortform",
    "shortForm",
    "short_form",
  ]);
  const courseShortForm = courseShortFormRaw || null;
  const courseCode = pickStringFromRecords([nested, root, courseRecord], [
    "courseCode",
    "course_code",
    "code",
  ]);
  const category = pickStringFromRecords([nested, root], [
    "category",
    "categoryName",
    "category_name",
    "seatCategory",
    "seat_category",
  ]);

  if (id === null && !courseName && !category) {
    return null;
  }

  const courseId =
    pickNumberFromRecords([nested, root, courseRecord], [
      "courseId",
      "course_id",
      "CourseId",
    ])
    ?? (
      spotDetailId != null
      && genericId != null
      && genericId !== spotDetailId
        ? genericId
        : null
    )
    ?? toIdNumber(nested.course)
    ?? toIdNumber(root.course);

  const vacantRaw =
    nested.vacantSeatsCount
    ?? nested.vacant_seats_count
    ?? nested.vacantSeats
    ?? nested.vacant_seats
    ?? nested.seats
    ?? root.vacantSeatsCount
    ?? root.vacant_seats_count;
  const vacantSeatsCount = toNumber(vacantRaw);
  const updatedBy = pickStringFromRecords([nested, root], ["updatedBy", "updated_by"]);
  const updatedOn = toUpdatedOnUtcValue(
    nested.updatedOn ?? nested.updated_on ?? root.updatedOn ?? root.updated_on,
  );

  return {
    id: id ?? 0,
    spotRoundId,
    exam,
    courseName,
    courseShortForm,
    courseCode,
    courseId,
    category,
    vacantSeatsCount,
    updatedBy: updatedBy || undefined,
    updatedOn: updatedOn || undefined,
  };
}

function parseViewerSpotsResponse(raw: unknown): ViewerSpotOut[] {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => normalizeViewerSpot(item))
      .filter((item): item is ViewerSpotOut => Boolean(item));
  }

  const root = asRecord(raw);
  if (!root) {
    throw new Error("Invalid spots response.");
  }

  const candidates = [
    root.spots,
    root.spotDetails,
    root.spot_details,
    root.content,
    root.items,
    root.data,
    root.results,
    asRecord(root.data)?.spots,
    asRecord(root.data)?.spotDetails,
    asRecord(root.data)?.spot_details,
    asRecord(root.data)?.content,
    asRecord(root.result)?.spots,
    asRecord(root.result)?.spotDetails,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate
        .map((item) => normalizeViewerSpot(item))
        .filter((item): item is ViewerSpotOut => Boolean(item));
    }
  }

  const single = normalizeViewerSpot(raw);
  if (single) {
    return [single];
  }

  throw new Error("Invalid spots response.");
}

function collectSpotsResponseRecords(raw: unknown): UnknownRecord[] {
  const root = asRecord(raw);
  if (!root) {
    return [];
  }

  return [root, asRecord(root.data), asRecord(root.result)].filter(
    (record): record is UnknownRecord => Boolean(record),
  );
}

function readSeatsPublishedFromSpotsPayload(
  raw: unknown,
  spots: ViewerSpotOut[],
): boolean | null {
  if (spots.length > 0) {
    return true;
  }

  const booleanKeys = [
    "seatsPublished",
    "seats_published",
    "hasPublishedSpots",
    "has_published_spots",
    "spotsPublished",
    "spots_published",
    "published",
    "seatInformationPublished",
    "seat_information_published",
  ];
  const countKeys = [
    "totalSpots",
    "total_spots",
    "spotCount",
    "spot_count",
    "publishedSpotCount",
    "published_spot_count",
  ];

  for (const record of collectSpotsResponseRecords(raw)) {
    for (const key of booleanKeys) {
      const value = record[key];
      if (value === true) {
        return true;
      }
      if (value === false) {
        return false;
      }
    }

    for (const key of countKeys) {
      const count = toNumber(record[key]);
      if (count !== null) {
        return count > 0;
      }
    }
  }

  return null;
}

export type ApplicantRoundSpotsResult = {
  spots: ViewerSpotOut[];
  /** Null when the response does not indicate whether seats were published. */
  seatsPublished: boolean | null;
};

/**
 * Lists SpotRounds for a college on the Viewer board.
 * GET /api/v1/spot/viewer/colleges/{collegeId}/rounds
 */
export async function fetchViewerCollegeRounds(
  collegeId: number | string,
): Promise<ViewerRoundOut[]> {
  const url = `/api/v1/spot/viewer/colleges/${encodeURIComponent(String(collegeId))}/rounds`;
  const requestUrl = `${String(httpClient.defaults.baseURL ?? "").replace(/\/$/, "")}${url}`;
  try {
    console.log("[SpotRound][ViewerRounds] request", {
      collegeId: String(collegeId),
      requestUrl,
      method: "GET",
    });

    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
        Authorization: spotViewerAuthorizationHeader(),
      },
    });

    console.log("[SpotRound][ViewerRounds] response", {
      collegeId: String(collegeId),
      requestUrl,
      status: response.status,
      body: response.data,
    });

    try {
      const parsed = parseViewerRoundsResponse(response.data);
      return sortSpotRoundsBySequenceAsc(parsed, "[SpotRound][ViewerRounds]");
    } catch (parseError) {
      console.error("[SpotRound][ViewerRounds] parsing error", {
        collegeId: String(collegeId),
        requestUrl,
        status: response.status,
        body: response.data,
        error: parseError,
      });
      throw parseError;
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("[SpotRound][ViewerRounds] request failed", {
        collegeId: String(collegeId),
        requestUrl,
        status: error.response?.status ?? null,
        code: error.code ?? null,
        message: error.message,
        body: error.response?.data ?? null,
        isNetworkError: !error.response,
      });
    } else {
      console.error("[SpotRound][ViewerRounds] unknown failure", {
        collegeId: String(collegeId),
        requestUrl,
        error,
      });
    }
    throw new Error(getViewerApiErrorMessage(error, "Failed to load rounds."));
  }
}

/**
 * Lists spot seat rows for a Viewer SpotRound.
 * GET /api/v1/spot/viewer/rounds/{roundId}/spots
 */
export async function fetchViewerRoundSpots(roundId: number): Promise<ViewerSpotOut[]> {
  const url = `/api/v1/spot/viewer/rounds/${encodeURIComponent(String(roundId))}/spots`;
  const requestUrl = `${String(httpClient.defaults.baseURL ?? "").replace(/\/$/, "")}${url}`;
  try {
    console.log("[SpotRound][ViewerSpots] request", {
      roundId: String(roundId),
      requestUrl,
      method: "GET",
    });

    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
        Authorization: spotViewerAuthorizationHeader(),
      },
    });

    console.log("[SpotRound][ViewerSpots] response", {
      roundId: String(roundId),
      requestUrl,
      status: response.status,
      body: response.data,
    });

    return parseViewerSpotsResponse(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("[SpotRound][ViewerSpots] request failed", {
        roundId: String(roundId),
        requestUrl,
        status: error.response?.status ?? null,
        body: error.response?.data ?? null,
      });
    }
    throw new Error(getViewerApiErrorMessage(error, "Failed to load spots."));
  }
}

/**
 * Lists spot seat rows for the logged-in Applicant's SpotRound.
 * GET /api/v1/applicants/me/spot-rounds/{roundId}/spots
 */
export async function fetchApplicantRoundSpots(roundId: number): Promise<ApplicantRoundSpotsResult> {
  const url = `/api/v1/applicants/me/spot-rounds/${encodeURIComponent(String(roundId))}/spots`;
  const requestUrl = `${String(httpClient.defaults.baseURL ?? "").replace(/\/$/, "")}${url}`;
  try {
    console.log("[SpotRound][ApplicantSpots] request", {
      roundId: String(roundId),
      requestUrl,
      method: "GET",
    });

    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("[SpotRound][ApplicantSpots] response", {
      roundId: String(roundId),
      requestUrl,
      status: response.status,
      body: response.data,
    });

    const spots = parseViewerSpotsResponse(response.data);
    return {
      spots,
      seatsPublished: readSeatsPublishedFromSpotsPayload(response.data, spots),
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("[SpotRound][ApplicantSpots] request failed", {
        roundId: String(roundId),
        requestUrl,
        status: error.response?.status ?? null,
        body: error.response?.data ?? null,
      });
    }
    throw new Error(getViewerApiErrorMessage(error, "Failed to load spots."));
  }
}

export function mapApiRoundStatus(status?: string): ViewerRoundStatus {
  if (!status?.trim()) {
    return "Not Started";
  }
  const normalized = status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (normalized === "active" || normalized === "started" || normalized === "in_progress") {
    return "Active";
  }
  if (normalized === "ended" || normalized === "completed" || normalized === "closed") {
    return "Ended";
  }
  if (normalized === "scheduled") {
    return "Not Started";
  }
  return "Not Started";
}

export function toDateInputValue(value?: string | null): string {
  if (!value?.trim()) {
    return "";
  }
  const trimmed = value.trim();
  const iso = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) {
    return iso[1];
  }
  const dmy = trimmed.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, "0");
    const month = dmy[2].padStart(2, "0");
    return `${dmy[3]}-${month}-${day}`;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Default round selection: first ACTIVE → first SCHEDULED/Not Started → latest CLOSED → first available.
 */
export function pickDefaultViewerRoundId(apiRounds: ViewerRoundOut[]): string | undefined {
  if (!apiRounds.length) {
    return undefined;
  }

  const normalizedStatus = (status?: string) =>
    (status ?? "").trim().toUpperCase().replace(/[\s-]+/g, "_");

  const active = apiRounds.find((round) => {
    const status = normalizedStatus(round.status);
    return status === "ACTIVE" || status === "STARTED" || status === "IN_PROGRESS";
  });
  if (active) {
    return String(active.id);
  }

  const scheduled = apiRounds.find((round) => {
    const status = normalizedStatus(round.status);
    return (
      status === "SCHEDULED"
      || status === "NOT_STARTED"
      || status === "NOTSTARTED"
      || status === ""
    );
  });
  if (scheduled) {
    return String(scheduled.id);
  }

  const closed = apiRounds.filter((round) => {
    const status = normalizedStatus(round.status);
    return status === "CLOSED" || status === "ENDED" || status === "COMPLETED";
  });
  if (closed.length) {
    const withEndDate = closed.filter((round) => round.endDate?.trim());
    if (withEndDate.length) {
      return String(
        withEndDate.reduce((latest, round) =>
          (round.endDate ?? "") > (latest.endDate ?? "") ? round : latest,
        ).id,
      );
    }
    return String(closed[closed.length - 1]?.id);
  }

  return String(apiRounds[0]?.id);
}

function normalizeLookupToken(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function mapApiExamToSection(exam?: string): SpotMatrixSection | null {
  if (!exam?.trim()) {
    return null;
  }
  const normalized = normalizeLookupToken(exam);
  if (
    normalized === "jee"
    || normalized === "jeeallindia"
    || normalized === "allindia"
    || normalized.includes("jee")
  ) {
    return "jee";
  }
  if (
    normalized === "mhtcet"
    || normalized === "cet"
    || normalized.includes("mhtcet")
    || normalized.includes("mht")
  ) {
    return "mhtcet";
  }
  return null;
}

function seatValueFromSpot(spot: ViewerSpotOut): ViewerSeatValue {
  const seats = spot.vacantSeatsCount;
  return seats === null || seats === undefined || !Number.isFinite(seats) ? "" : seats;
}

export function blankSeatMatrix(): ViewerSeatMatrix {
  return { jee: {}, mhtcet: {} };
}

/** Column headers from matrix course keys (API `courseName` order). */
export function streamsFromSeatMatrix(matrix: ViewerSeatMatrix): string[] {
  for (const section of ["jee", "mhtcet"] as const) {
    const firstCategory = Object.keys(matrix[section])[0];
    if (firstCategory) {
      return Object.keys(matrix[section][firstCategory] ?? {});
    }
  }
  return [];
}

/**
 * Fold flat spots API rows into the seat matrix table shape.
 * Rows = API category, columns = unique courseName, cells = vacantSeatsCount.
 */
export function mapApiSpotsToSeatMatrix(spots: ViewerSpotOut[]): {
  matrix: ViewerSeatMatrix;
  courseShortForms: Record<string, string>;
  columns: string[];
  rows: { section: SpotMatrixSection; categories: string[] }[];
} {
  const matrix: ViewerSeatMatrix = blankSeatMatrix();
  const courseNames: string[] = [];
  const courseSeen = new Set<string>();
  const courseShortForms: Record<string, string> = {};
  const categoriesBySection: Record<SpotMatrixSection, string[]> = { jee: [], mhtcet: [] };
  const categorySeen: Record<SpotMatrixSection, Set<string>> = {
    jee: new Set(),
    mhtcet: new Set(),
  };
  const cellValues = new Map<string, ViewerSeatValue>();

  for (const spot of spots) {
    const section = mapApiExamToSection(spot.exam);
    if (!section) {
      continue;
    }

    const category = spot.category?.trim();
    if (!category) {
      continue;
    }

    const courseName = spot.courseName?.trim();
    if (!courseName) {
      continue;
    }

    if (!courseSeen.has(courseName)) {
      courseSeen.add(courseName);
      courseNames.push(courseName);
      const courseShortFormLabel = spot.courseShortForm?.trim();
      if (courseShortFormLabel) {
        courseShortForms[courseName] = courseShortFormLabel;
      }
    }
    if (!categorySeen[section].has(category)) {
      categorySeen[section].add(category);
      categoriesBySection[section].push(category);
    }

    const cellKey = `${section}\0${category}\0${courseName}`;
    cellValues.set(cellKey, seatValueFromSpot(spot));
  }

  for (const section of ["jee", "mhtcet"] as const) {
    for (const category of categoriesBySection[section]) {
      const row: Record<string, ViewerSeatValue> = {};
      for (const courseName of courseNames) {
        row[courseName] = cellValues.get(`${section}\0${category}\0${courseName}`) ?? "";
      }
      matrix[section][category] = row;
    }
  }

  const rows = (["jee", "mhtcet"] as const)
    .filter((section) => categoriesBySection[section].length > 0)
    .map((section) => ({
      section,
      categories: categoriesBySection[section],
    }));

  return { matrix, courseShortForms, columns: courseNames, rows };
}
